import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { productService, Product } from '../services/productService';
import { useAppDispatch, useAppSelector } from '../../../shared/store/hooks';
import { addToCart } from '../../../shared/store/cartSlice';
import { toggleWishlist, selectIsWishlisted } from '../../../shared/store/wishlistSlice';

const API_STATIC = (process.env.REACT_APP_API_URL ?? 'http://localhost:8000/api/v1').replace(/\/api\/v1.*$/, '');
const PAGE_LIMIT = 12;

const ArrowRight: React.FC = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
);

const ProductsPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const paramCategory = searchParams.get('category') ?? 'All';
    const paramPage     = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10));

    const [products, setProducts]     = useState<Product[]>([]);
    const [categories, setCategories] = useState<string[]>([]);
    const [total, setTotal]           = useState(0);
    const [loading, setLoading]       = useState(true);

    const fetchProducts = useCallback(async (category: string, page: number) => {
        setLoading(true);
        try {
            const query = category === 'All'
                ? { limit: PAGE_LIMIT, page }
                : { limit: PAGE_LIMIT, page, category };
            const res = await productService.getPublicAll(query);
            setProducts(res.data.products);
            setTotal(res.data.total);
            if (res.data.categories?.length) setCategories(res.data.categories);
        } catch {
            setProducts([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProducts(paramCategory, paramPage);
    }, [paramCategory, paramPage, fetchProducts]);

    const handleTabClick = (cat: string) => {
        setSearchParams({ category: cat, page: '1' });
    };

    const handlePage = (p: number) => {
        setSearchParams({ category: paramCategory, page: String(p) });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const totalPages = Math.ceil(total / PAGE_LIMIT) || 1;

    return (
        <div style={{ background: '#f8fafc', minHeight: '60vh', padding: '48px 0 72px', fontFamily: "'Open Sans', sans-serif" }}>
            <div className="container">

                {/* Breadcrumb */}
                <nav style={{ marginBottom: 28, fontSize: '0.85rem', color: '#6b7280' }}>
                    <Link to="/" style={{ color: '#1565c0', textDecoration: 'none' }}>Home</Link>
                    <span style={{ margin: '0 8px' }}>›</span>
                    <span style={{ color: '#1a202c', fontWeight: 600 }}>Products</span>
                </nav>

                {/* Header */}
                <h1 style={{ fontSize: '2rem', fontWeight: 700, color: '#1a202c', margin: '0 0 6px' }}>
                    Our Product Catalogue
                </h1>
                <p style={{ color: '#64748b', marginBottom: 32 }}>
                    {total > 0 ? `${total} products available` : 'Browse our handcrafted collection'}
                </p>

                {/* Category tabs */}
                {categories.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 36 }}>
                        {['All', ...categories].map(cat => (
                            <button
                                key={cat}
                                onClick={() => handleTabClick(cat)}
                                style={{
                                    padding: '7px 20px',
                                    borderRadius: 50,
                                    border: `1.5px solid ${paramCategory === cat ? '#1565c0' : '#d1d5db'}`,
                                    background: paramCategory === cat ? '#1565c0' : '#fff',
                                    color: paramCategory === cat ? '#fff' : '#374151',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    transition: 'all 0.15s',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                )}

                {/* Grid */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                    gap: 24,
                    marginBottom: 48,
                }}>
                    {loading
                        ? Array.from({ length: PAGE_LIMIT }).map((_, i) => (
                            <div key={i} style={{ borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', background: '#fff' }}>
                                <div style={{ aspectRatio: '1/1', background: 'linear-gradient(90deg,#f0f4f8 25%,#e2e8f0 50%,#f0f4f8 75%)', backgroundSize: '200% 100%', animation: 'pcat-shimmer 1.4s infinite' }} />
                                <div style={{ padding: 16 }}>
                                    <div style={{ height: 10, borderRadius: 5, background: '#e2e8f0', marginBottom: 8, width: '40%' }} />
                                    <div style={{ height: 12, borderRadius: 5, background: '#e2e8f0', marginBottom: 6, width: '80%' }} />
                                    <div style={{ height: 10, borderRadius: 5, background: '#e2e8f0', width: '55%' }} />
                                </div>
                            </div>
                        ))
                        : products.length === 0
                            ? (
                                <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
                                    No products found in this category yet.
                                </div>
                            )
                            : products.map(p => <ProductCard key={p._id} product={p} />)
                    }
                </div>

                {/* Pagination */}
                {!loading && totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
                        <button
                            onClick={() => handlePage(paramPage - 1)}
                            disabled={paramPage <= 1}
                            style={paginBtn(paramPage <= 1)}
                        >
                            ← Prev
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(p => p === 1 || p === totalPages || Math.abs(p - paramPage) <= 2)
                            .map((p, idx, arr) => (
                                <React.Fragment key={p}>
                                    {idx > 0 && arr[idx - 1] !== p - 1 && (
                                        <span style={{ color: '#94a3b8', padding: '0 4px' }}>…</span>
                                    )}
                                    <button
                                        onClick={() => handlePage(p)}
                                        style={paginBtn(false, p === paramPage)}
                                    >
                                        {p}
                                    </button>
                                </React.Fragment>
                            ))
                        }
                        <button
                            onClick={() => handlePage(paramPage + 1)}
                            disabled={paramPage >= totalPages}
                            style={paginBtn(paramPage >= totalPages)}
                        >
                            Next →
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

function paginBtn(disabled: boolean, active?: boolean): React.CSSProperties {
    return {
        padding: '7px 14px',
        borderRadius: 8,
        border: `1.5px solid ${active ? '#1565c0' : '#d1d5db'}`,
        background: active ? '#1565c0' : disabled ? '#f9fafb' : '#fff',
        color: active ? '#fff' : disabled ? '#9ca3af' : '#374151',
        fontSize: '0.875rem',
        fontWeight: 500,
        cursor: disabled ? 'default' : 'pointer',
    };
}

const ProductCard: React.FC<{ product: Product }> = ({ product: p }) => {
    const dispatch = useAppDispatch();
    const [added, setAdded] = useState(false);
    const wishlisted = useAppSelector(selectIsWishlisted(p._id));

    const handleAddToCart = () => {
        dispatch(addToCart({ productId: p._id, name: p.name, brand: p.brand, price: p.price, image: p.image, category: p.category }));
        setAdded(true);
        setTimeout(() => setAdded(false), 1500);
    };

    const handleWishlist = (e: React.MouseEvent) => {
        e.preventDefault();
        dispatch(toggleWishlist({ productId: p._id, name: p.name, brand: p.brand, price: p.price, image: `${API_STATIC}${p.image}`, category: p.category }));
    };

    return (
        <div style={{
            borderRadius: 12,
            border: '1px solid #e2e8f0',
            background: '#fff',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            transition: 'box-shadow 0.2s, transform 0.2s',
        }}
            onMouseEnter={e => {
                (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 24px rgba(21,101,192,0.12)';
                (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-3px)';
            }}
            onMouseLeave={e => {
                (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
                (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
            }}
        >
            <div style={{ aspectRatio: '1/1', overflow: 'hidden', background: '#f8fafc', position: 'relative' }}>
                <img
                    src={`${API_STATIC}${p.image}`}
                    alt={p.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={e => {
                        (e.target as HTMLImageElement).src =
                            'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"%3E%3Crect width="200" height="200" fill="%23f0f4f8"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="14"%3ENo image%3C/text%3E%3C/svg%3E';
                    }}
                />
                <button
                    onClick={handleWishlist}
                    title={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                    style={{ position: 'absolute', top: 8, right: 8, width: 30, height: 30, borderRadius: '50%', background: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.95rem', boxShadow: '0 1px 4px rgba(0,0,0,0.15)', color: wishlisted ? '#ef4444' : '#94a3b8', transition: 'color 0.15s' }}
                >
                    {wishlisted ? '♥' : '♡'}
                </button>
            </div>
            <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#1565c0', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
                    {p.category}
                </span>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1a202c', margin: '0 0 2px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {p.name}
                </h3>
                <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 12px' }}>{p.brand}</p>
                <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <span style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a202c' }}>
                            ₹{p.price.toLocaleString('en-IN')}
                        </span>
                        <Link
                            to={`/products/${p._id}`}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                padding: '6px 14px', background: '#1565c0', color: '#fff',
                                borderRadius: 8, fontSize: '0.82rem', fontWeight: 600,
                                textDecoration: 'none',
                            }}
                        >
                            View <ArrowRight />
                        </Link>
                    </div>
                    <button
                        onClick={handleAddToCart}
                        style={{
                            width: '100%', padding: '7px 0',
                            border: `1.5px solid ${added ? '#16a34a' : '#1565c0'}`,
                            background: added ? '#f0fdf4' : '#fff',
                            color: added ? '#16a34a' : '#1565c0',
                            borderRadius: 8, fontSize: '0.82rem', fontWeight: 600,
                            cursor: 'pointer', transition: 'all 0.15s',
                        }}
                    >
                        {added ? '✓ Added to Cart' : '+ Add to Cart'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProductsPage;
